import { useMemo } from "react"
import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"
import { Check, X } from "lucide-react"

interface PasswordStrengthIndicatorProps {
  password: string
  className?: string
}

interface PasswordRequirement {
  label: string
  test: (password: string) => boolean
}

const requirements: PasswordRequirement[] = [
  { label: "Al menos 8 caracteres", test: (p) => p.length >= 8 },
  { label: "Una letra mayúscula", test: (p) => /[A-Z]/.test(p) },
  { label: "Una letra minúscula", test: (p) => /[a-z]/.test(p) },
  { label: "Un número", test: (p) => /\d/.test(p) },
  { label: "Un carácter especial (@$!%*?&)", test: (p) => /[@$!%*?&]/.test(p) },
]

export function PasswordStrengthIndicator({ password, className }: PasswordStrengthIndicatorProps) {
  const analysis = useMemo(() => {
    const passed = requirements.filter((req) => req.test(password))
    const score = (passed.length / requirements.length) * 100

    let strength: "weak" | "fair" | "good" | "strong"
    let label: string

    if (score <= 20) {
      strength = "weak"
      label = "Muy débil"
    } else if (score <= 40) {
      strength = "weak"
      label = "Débil"
    } else if (score <= 60) {
      strength = "fair"
      label = "Regular"
    } else if (score <= 80) {
      strength = "good"
      label = "Buena"
    } else {
      strength = "strong"
      label = "Fuerte"
    }

    return { score, strength, label, passed }
  }, [password])

  if (!password) return null

  return (
    <div className={cn("space-y-3", className)}>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Fortaleza:</span>
          <span
            className={cn(
              "font-medium",
              analysis.strength === "weak" && "text-destructive",
              analysis.strength === "fair" && "text-yellow-500",
              analysis.strength === "good" && "text-blue-500",
              analysis.strength === "strong" && "text-green-500",
            )}
          >
            {analysis.label}
          </span>
        </div>
        <Progress
          value={analysis.score}
          className={cn(
            "h-2",
            analysis.strength === "weak" && "[&>div]:bg-destructive",
            analysis.strength === "fair" && "[&>div]:bg-yellow-500",
            analysis.strength === "good" && "[&>div]:bg-blue-500",
            analysis.strength === "strong" && "[&>div]:bg-green-500",
          )}
        />
      </div>

      <ul className="grid gap-1.5 text-sm">
        {requirements.map((req, index) => {
          const isPassed = req.test(password)
          return (
            <li
              key={index}
              className={cn(
                "flex items-center gap-2 transition-colors",
                isPassed ? "text-green-500" : "text-muted-foreground",
              )}
            >
              {isPassed ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
              {req.label}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
